import { Component } from "vue";

export interface Account {
  id: number;
  email: string;
  name: string;
  sub: string;
  organization: string;
  avatar: string;
  token: string | null;
  roles: {
    system_admin: boolean;
    organization_admin: boolean;
  };
  permissions: string[];
}

export interface Configuration {
  description: string;
  id: string;
  label: string;
  permissions: string[];
  roles: {
    organization_admin: boolean;
    system_admin: boolean;
  };
  type: string;
  url: string;
}

export interface PersistedExportSelection {
  //can later be extended to save more data
  options: {
    exporter: ExportType;
    customSettings: CustomExportSettings;
  };
}

export interface ExportSelection {
  uuid: string;
  frame: number;
  length: number;
  options: CustomExportOptions;
}

export interface ExportEditorEvents {
  [event: string]: (...args: any) => void;
  frame: (frame: number) => number;
}

export interface BvhInfo {
  frames: number;
  frameTime: number;
  fps: number;
}

export enum LicenseType {
  NONE,
  DEVELOPER,
  EXTENDED
}

export interface MVNXInfo {
  key: string;
  value: boolean;
  licenseRequired: LicenseType;
}

export interface TreeObject<T> {
  name: string;
  isLeaf: boolean;
  disabled: boolean;
  dragDisabled: boolean;
  children: TreeObject<T>[];
  data: T;
}

export interface PageMenuItem {
  id: string;
  icon: string;
  title: string;
}

export interface FieldDefinition {
  name: string | Component;
  title?: string | Function;
  sortField?: string;
  dataClass?: string;
  titleClass?: string;
  formatter?: Function;
  visible?: boolean;
  hideable?: boolean;
  width?: string;
  $_index?: number;
}

export interface ActionDefinition {
  event: string;
  label: string;
  icon: string;
  component?: Component;
}

export enum ExportType {
  FBX = "fbx",
  BVH = "bvh",
  MVNX = "mvnx"
}

export enum TrimType {
  ABSTIME = "abstime",
  RELTIME = "reltime",
  FRAMENR = "framenr"
}

export enum TaskType {
  FILE_EXPORT = "process-file-export",
  HD_REPROCESSING = "process-hd-reprocessing"
}

export enum StorageContainer {
  MVN = "mvn",
  BVH = "bvh",
  FBX = "fbx",
  MVNX = "mvnx",
  XSLX = "xslx"
}

export interface EditFile {
  filename: string;
  tags: Array<{ label: string }>;
  folderId: number | null;
}

export interface FileUploadResponse {
  fileUuid: string | null;
  file: FileListFile;
}

export interface FileListFile extends Blob {
  readonly lastModified: number;
  readonly name: string;
}

export interface ProcessCommand {
  task: TaskType;
}

export interface FileUploadAndReprocessCommand extends ProcessCommand {
  options: ReprocessingOptions;
}

export interface FileExportCommand extends ProcessCommand {
  fileUuid: string;
  exportType: ExportType;
  options: CustomExportOptions;
}

export interface CustomExportOptions {
  trim?: TrimType;
  start?: number;
  end?: number;
  output: string;
  exporter: ExportType;
  customSettings?: CustomExportSettings;
}

export interface CustomExportSettings {
  [key: string]: string | number | boolean;
}

export interface GraphqlResponse {
  __typename: string;
}

export enum Processingstate {
  Processing = "PROCESSING",
  Available = "AVAILABLE",
  Failed = "FAILED"
}

export interface Input {
  [key: string]: string;
}

export interface File extends GraphqlResponse {
  id: number;
  name: string;
  fileUuid: string;
  uuid: string;
  sourceId: number | null;
  source_uuid: string | null;
  folderId: number | null;
  tags: Array<string>;
  input: Input;
  state: Processingstate;
  source: File;
  storage: Storage;
  exports: Array<object>;
  owner: Owner;
}

export interface PaginatedExports {
  meta: any;
  items: Array<object>;
}

export interface Recording extends GraphqlResponse {
  id: number;
  name: string;
  fileUuid: string;
  uuid: string;
  tags: Array<string>;
  state: Processingstate;
  exports: PaginatedExports;
  owner: Owner;
}

export interface Storage extends GraphqlResponse {
  id: number;
  fileUuid: string;
  filename: string;
  account: Owner;
  container: StorageContainer;
}

export interface Folder extends GraphqlResponse {
  id: number;
  parentId: number | null;
  parent_id: number | null;
  label: string;
  parent: Folder | null;
  owner: Owner;
  ancestors: Partial<Folder>[];
}

export interface Tag extends GraphqlResponse {
  label: string;
}

export interface Owner extends GraphqlResponse {
  id: number;
  name: string;
  email: string;
  sub: string;
  groupId: string;
}

export enum FileType {
  mvnx = ".mvnx",
  mvn = ".mvn"
}

/**
 * Reprocess Quality Options
 * --quality=
 * Set the quality for the reprocessed file.
 * Options fast | full
 * @readonly
 * @enum {boolean}
 */
export enum Quality {
  FAST = "fast",
  FULL = "full"
}

/**
 * Scenario options
 *  --scenario=
 * Set the scenario for the file (single file processing only).
 * Options vary, typical options: singleLevel | multiLevel | noLevel
 * @readonly
 * @enum {string}
 */
export enum Scenario {
  SINGLE_LEVEL = "singleLevel",
  MULTI_LEVEL = "multiLevel",
  NO_LEVEL = "noLevel",
  SOFT_FLOOR = "softFloor",
  VEHICLE = "vehicle"
}

/**
 * Position aiding options
 * --positionaiding=
 * Explicitly turn on/off the 3d position aiding, overriding the settings in the file.
 * Options: on/off
 * @readonly
 * @enum {boolean}
 */
export enum PositionAiding {
  OFF,
  ON
}

/**
 * No reset options
 *  --noreset
 * Remove all of the above resets from the file if already in there (single file processing only),
 * implied by any of the above resets.
 * @readonly
 * @enum {boolean}
 */
export enum NoReset {
  ON = 1,
  OFF = 0
}

/**
 * Reset engine option
 * --resetengine
 * Reset the motion capture engine at the start of reprocessing (single file processing only),
 * implies --resetposition
 * @readonly
 * @enum {boolean}
 */
export enum ResetEngine {
  ON = 1,
  OFF = 0
}

/**
 * Reset heading option
 * --resetheading
 * Reset the heading at the start of reprocessing (single file processing only)
 * @readonly
 * @enum {boolean}
 */
export enum ResetHeading {
  ON = 1,
  OFF = 0
}

/**
 * Reset position option
 * --resetposition
 * Reset the position at the start of reprocessing (single file processing only)
 * @readonly
 * @enum {boolean}
 */
export enum ResetPosition {
  ON = 1,
  OFF = 0
}

/**
 * Reprocessing options
 * @typedef {Object} ReprocessingOptions
 * @property {Quality} quality
 * @property {Scenario} scenario
 * @property {ResetEngine} resetengine
 * @property {ResetHeading} resetheading
 * @property {ResetPosition} resetposition
 * @property {PositionAiding} positionaiding
 * @property {NoReset} noreset
 */
export type ReprocessingOptions = {
  quality: Quality;
  scenario: Scenario;
  resetengine: ResetEngine;
  resetheading: ResetHeading;
  resetposition: ResetPosition;
  positionaiding: PositionAiding;
  noreset: NoReset;
};

export interface Quota {
  usage_quotum: number;
  usage_used: number;
  quotum_group: QuotaUsageType;
}

export enum QuotaUsageType {
  SECONDS = "seconds",
  REPORTS = "reports"
}

export type RedirectRoute = Partial<Location>;
